// CFG-IA.B1 (#1541) — Settings → Storage.
//
// ADMIN-ONLY per the #1595 hybrid Member-Settings-Visibility decision — this
// surface used to be member-read/admin-write (D13), but it embeds a real
// write path (Drive OAuth, /settings/integrations/gdrive) that a read-only
// presentation cannot safely expose to non-admins (#1594: a TENANT_MEMBER
// could reach and initiate tenant-wide Drive authorization from here despite
// the page's own "view only" messaging). Fail-CLOSED, modeled on
// SettingsInfrastructurePage: a null/failed membership or non-admin role
// redirects to /settings, never falls through to the storage surface.
// bootstrap-agent.ts:295 reads storageProvider from tenant_infrastructure_configs
// to dispatch GOOGLE_DRIVE vs SHAREPOINT for CREATE_FOLDER — this page is the
// settings write path that the wizard's StorageProviderStep also uses.
// DRV-5a (#2883): the Google Drive card shows the live connection status
// (DriveStatusSummary, GET /tenant-integrations/me) instead of static text.

import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import StorageProviderCard from '../components/settings/StorageProviderCard';
import DriveStatusSummary from '../components/settings/DriveStatusSummary';
import FoldersNeedingAttention from '../components/settings/FoldersNeedingAttention';
import { useMyTenant, isAdminRole } from '../hooks/useMyTenant';

const cardStyle: React.CSSProperties = {
  background:    'var(--surface)',
  border:        '1px solid var(--border)',
  borderRadius:  'var(--radius-md)',
  padding:       20,
  marginBottom:  16,
};

export default function SettingsStoragePage() {
  const navigate = useNavigate();
  const { myTenant, loading: tenantLoading } = useMyTenant();
  const tenantId = myTenant?.tenantId;
  const isAdmin  = isAdminRole(myTenant?.role);

  // Fail CLOSED — a non-admin OR a null/failed membership redirects to
  // /settings rather than rendering the storage surface.
  useEffect(() => {
    if (!tenantLoading && !isAdmin) navigate('/settings');
  }, [tenantLoading, isAdmin, navigate]);

  // Fail-closed placeholder: non-admin / null membership is being redirected.
  if (!isAdmin || !tenantId) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
        <Sidebar />
        <main style={{ flex: 1, padding: 32 }}>
          <p data-testid="storage-redirecting" style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)' }}>
            Redirecting…
          </p>
        </main>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }} data-testid="storage-page">
      <Sidebar />
      <main style={{ flex: 1, padding: 32, maxWidth: 760 }}>
        <div style={{ marginBottom: 16 }}>
          <Link
            to="/settings"
            style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}
          >
            ← Back to settings
          </Link>
        </div>

        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
          Storage
        </h1>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
          Choose where BluePrint stores project artefacts and shared documents.
        </p>

        <div style={cardStyle}>
          <StorageProviderCard
            tenantId={tenantId}
            canEdit={isAdmin}
            showIntro={false}
          />
        </div>

        <div style={{ ...cardStyle, marginTop: 16 }} data-testid="panel-drive">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
            Google Drive authorisation
          </h3>
          <DriveStatusSummary />
        </div>

        {/* DRV-5b(i) (#2883) — renders nothing unless a folder needs a reset. */}
        <FoldersNeedingAttention />
      </main>
    </div>
  );
}
